import * as React from 'react';
import Box from '@mui/material/Box';
import { columns, generateRows } from './partials';
import { Table } from '@organisms';
import { callDeleteCoreOfficeAddress, callGetCoreOfficeAddress } from '@api';
import { useState, useContext, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { TableContext } from '@particles';
import { Alert, Button, Text, TextView } from '@atoms';
import { Stack } from '@mui/material';
import AddOffice from './addOffices';
import UploadOfficesCSV from './uploadOfficesCSV';
import { isFieldVisible, resolveBusinessType, OfficeFieldKey } from './officeFieldVisibility';

const CoreOffices: React.FC<{ locationId: string; businessType?: string | null; integration?: string | null }> = ({
  locationId,
  businessType,
  integration
}) => {
  const {
    state: {
      coreOfficesPagination: { page, perPage, orderBy }
    }
  } = useContext(TableContext);
  const [openOfficeAddress, setOfficeAddress] = useState<boolean>(false);
  const [selectOfficeAddress, setSelectOfficeAddress] = useState<CoreOfficeAddress>();
  const [openOfficeUpload, setOfficeUpload] = useState<boolean>(false);
  const [csvLoading, setCsvLoading] = useState<boolean>(false);
  const [error] = useState<string>();

  const officesQuery = useQuery({
    queryKey: ['core-offices', locationId, page, perPage, orderBy],
    queryFn: () => callGetCoreOfficeAddress({ locationId, pageNumber: page, pageSize: perPage, sortBy: orderBy }),
    placeholderData: keepPreviousData
  });

  const data: Pagination<CoreOfficeAddress> | undefined =
    officesQuery.data?.code === 200 ? officesQuery.data.data : undefined;
  const loading = officesQuery.isFetching || csvLoading;
  const refetch = () => officesQuery.refetch();

  async function onDeleteOffice(office: CoreOfficeAddress) {
    if (!office.id) return;
    const { code } = await callDeleteCoreOfficeAddress({
      locationId,
      officeAddressId: office.id
    });
    if (code == 200) {
      refetch();
    }
  }

  const downloadFile = async () => {
    setCsvLoading(true);
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_BASE;
      const urlApi = `${apiBase}/core/location/${locationId}/offices-address/export`;

      const fetchOptions: RequestInit = {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        method: 'GET'
      };

      const response: Response = await fetch(urlApi, fetchOptions);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'CoreOffices.csv');
      document.body.appendChild(link);
      link.click();
    } finally {
      setCsvLoading(false);
    }
  };

  const rowData: TableRow[] = data
    ? generateRows(data.data, (type, office) => {
        if (type === 'edit') {
          setSelectOfficeAddress(office);
          setOfficeAddress(true);
        } else if (type === 'delete') {
          onDeleteOffice(office);
        }
      })
    : [];

  // The Branch column maps to the `type` field key in the visibility config.
  const visibleColumns = useMemo(() => {
    const bt = resolveBusinessType(businessType);
    return columns.filter((col) => {
      if (col.accessor === 'action') return true;
      return isFieldVisible(bt, col.accessor as OfficeFieldKey, integration);
    });
  }, [businessType, integration]);

  return (
    <Box>
      <Box
        sx={{
          flexDirection: 'row',
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          justifyContent: 'space-between'
        }}>
        <TextView display={'initial'} $fontSize={22}/>
        <Stack spacing={2} direction={'row'}>
          <Button
            variant="contained"
            onClick={() => {
              setSelectOfficeAddress(undefined);
              setOfficeAddress(true);
            }}>
            <Text>Add Offices</Text>
          </Button>
          <Button variant="contained" onClick={() => setOfficeUpload(true)}>
            <Text>Upload Offices</Text>
          </Button>
          <Button variant="contained" onClick={async () => await downloadFile()} disabled={loading}>
            <Text>Export</Text>
          </Button>
        </Stack>
      </Box>
      {error && (
        <Box sx={{ mb: 1 }}>
          <Alert message={error} severity={'error'} />
        </Box>
      )}

      <Table
        name="core_offices"
        columns={visibleColumns}
        data={rowData}
        isLoading={loading}
        pagination={data}
        persistColumnPrefs
      />
      <UploadOfficesCSV
        locationId={locationId}
        open={openOfficeUpload}
        onClose={function (): void {
          setOfficeUpload(false);
        }}
        onSuccess={function (message?: string): void {
          setOfficeUpload(false);
          refetch();
        }}
      />
      <AddOffice
        locationId={locationId}
        businessType={businessType}
        integration={integration}
        open={openOfficeAddress}
        selectedOfficeAddress={selectOfficeAddress}
        onClose={function (): void {
          setOfficeAddress(false);
        }}
        onSuccess={function (): void {
          setOfficeAddress(false);
          refetch();
        }}
      />
    </Box>
  );
};

export default CoreOffices;
